import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { DatePipe } from '@angular/common';

import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

import { DataService, HelperService, HTTPService, DateService, CalculationService } from 'app/services';

import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';

import { messages } from 'app/constants';
import { currency } from 'app/constants/currency.constants';
import { datetimeFormat } from 'app/constants/date-time-format';

import { environment } from 'environments/environment';

import * as moment from 'moment';
import { find, debounce } from 'lodash';

interface ISubletItem {
  isChecked: boolean;
  title: string;
  cost: number;
  price: number;
  isTaxable: boolean;
  count: number;
  subTotal: number;
  totalCost: number;
  workStatus: any;
  cartId: string;
  itemId: string;
  itemInternalReference: string;
  salesCode: string;
}
interface ITotals {
  subTotal: number;
  tax: any;
  taxedAmount: number;
  nonTaxableAmount: number;
  taxableAmount: number;
  total: number;
}
interface ISubSubletGroup {
  subletItems: ISubletItem[];
  categoryStatus: string;
  cartId: string;
  isAllChecked: boolean;
  isSomeChecked: boolean;
  isSomeCompleted: boolean;
  isAllCompleted: boolean;
  isSubmitting: boolean;
  isUpdating: boolean;
  isPosting: boolean;
  paidStatus: string;
  invoiceDate: any;
  dueDate: any;
  supplierInvoicedNo: string;
  qboId: string;
  isSyncedWithQBO: boolean;
  allWorkStatus: any;
  totals: ITotals;
  invoiceDateErrMsg: string;
  dueDateErrMsg: string;
  invoiceNoErrMsg: string;
  qboOptions: any[];
  cartTotals: any;
  paidDateTimeTooltip: string;
}
interface ISubletGroup {
  supplier: {
    name: string;
    supplierId: string;
  };
  supplierGroupId: string;
  accPayableTerm: any;
  notes: string;
  subGroups: ISubSubletGroup[];
  groupCategoryStatus: string;
  supplierErrMsg: string;
  paymentTermErrMsg: string;
  isSuplierTouched: boolean;
  oldSupplierId: string;
}

@Component({
  selector: 'app-sublet-supplier-dialog',
  templateUrl: './sublet-supplier-dialog.component.html',
  styleUrls: ['./sublet-supplier-dialog.component.scss'],
  providers: [DialogService],
})
export class SubletSupplierDialogComponent implements OnInit {
  @ViewChild('subletSupplierBody', { static: true }) subletSupplierBody: ElementRef;
  isLoadingSuppliers: boolean = false;
  suppliersResults: any[] = [];
  suppliers: any[] = [];
  paymentTerms: any[] = [];
  isQboConnected: boolean = false;
  isAccountPayable: boolean = false;
  taxableOptions: any[] = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];
  assignStatusOptions: any[] = [
    { label: 'Not started', value: 'notStarted' },
    { label: 'In progress', value: 'inProgress' },
    { label: 'Completed', value: 'completed' },
  ];
  roTitle: string = '';
  roNum: string = '';
  code: string = '';
  usd: string = currency.USD;
  subletGroupsArray: ISubletGroup[] = [];
  orderId: string = '';
  isLoading: boolean = false;
  isReloadNeeded: boolean = false;
  subletPricing: any = {};
  hasUnsavedChanges: boolean = false;
  itemReferenceId: string = '';
  isSalesCodeEnabled: boolean = false;
  invoicedRO: boolean = false;

  constructor(
    private httpService: HTTPService,
    private helperService: HelperService,
    private dynamicDialogRef: DynamicDialogRef,
    private dynamicDialogConfig: DynamicDialogConfig,
    private dataService: DataService,
    private dateService: DateService,
    private calculationService: CalculationService,
    private dialogService: DialogService,
    private datePipe: DatePipe,
  ) {}

  ngOnInit(): void {
    const { roTitle, code, orderId, subletPricing, itemReferenceId, roNum, invoicedRO } = this.dynamicDialogConfig.data;
    this.roTitle = roTitle || '';
    this.roNum = roNum || '';
    this.code = code;
    this.orderId = orderId;
    this.subletPricing = this.helperService.deepCopy(subletPricing);
    this.itemReferenceId = itemReferenceId || '';
    this.invoicedRO = invoicedRO;
    this.setFeatureSettings();
    this.getSuppliers();
    this.getSubletItems(this.orderId);
    this.isQboConnected = !!this.helperService.getCompany().qbo;
  }

  handleClose(isCloseForcefully: boolean = false) {
    if (this.hasUnsavedChanges && !isCloseForcefully) {
      this.handleDiscardChanges();
    } else {
      this.dynamicDialogRef.close({ data: { isReloadNeeded: this.isReloadNeeded } });
    }
  }

  mapSelectedSubletItems(cartGroup: ISubSubletGroup, subletGroup?: ISubletGroup, isCompletedOnly?: boolean) {
    let itemsObj: any = {};
    cartGroup.subletItems.forEach((item: ISubletItem) => {
      if (isCompletedOnly) {
        if (item.isChecked && item.workStatus?.value === 'completed') {
          if (!itemsObj[item.itemInternalReference]) {
            itemsObj[item.itemInternalReference] = {};
          }
          itemsObj[item.itemInternalReference] = {
            cost: item.cost,
            isTaxable: item.isTaxable,
            totalCost: item.cost * item.count,
            workStatus: item.workStatus?.value,
            price: this.calculatePrice(item.count, item.price),
            count: item.count,
          };
          if (this.isSalesCodeEnabled && item?.salesCode) {
            itemsObj[item.itemInternalReference].salesCode = item.salesCode;
          }
        }
      } else {
        if (item.isChecked) {
          if (!itemsObj[item.itemInternalReference]) {
            itemsObj[item.itemInternalReference] = {};
          }
          itemsObj[item.itemInternalReference] = {
            cost: item.cost,
            isTaxable: item.isTaxable,
            totalCost: item.cost * item.count,
            workStatus: item.workStatus?.value,
            price: this.calculatePrice(item.count, item.price),
            count: item.count,
          };
          if (this.isSalesCodeEnabled && item?.salesCode) {
            itemsObj[item.itemInternalReference].salesCode = item.salesCode;
          }
        }
      }
    });
    return itemsObj;
  }

  calculatePricingMatrix(cost: number): number {
    if (this.subletPricing && this.subletPricing.sublet && cost > 0) {
      const markup = this.subletPricing.sublet.markup;
      let price: any = this.calculationService.calculateMarkUpPrice(cost, markup);
      price = this.calculationService.roundOff(price);
      return price;
    }
  }

  calculatePrice(count: number, price: number) {
    // if (this.subletPricing && this.subletPricing.sublet && cost > 0) {
    //   const markup = this.subletPricing.sublet.markup;
    //   let price: any = this.calculationService.calculateMarkUpPrice(cost, markup);
    //   price = this.calculationService.roundOff(price);
    //   return price;
    // }

    /* The price calculation that has been done is not applied to the service items
    on the RO (Repair Order). The price calculation is done
    normally, and it should be the same as it is for the RO service items.*/
    let subletPrice: any = price;
    return subletPrice;
  }

  handleSubletItemChange(key: string, event: any, subletItem: ISubletItem, subletSubGroup: ISubSubletGroup) {
    subletItem[key] = event;
    if (['isChecked', 'workStatus', 'allWorkStatus'].includes(key)) {
      subletSubGroup.isAllChecked = subletSubGroup.subletItems.every((item: ISubletItem) => item.isChecked);
      subletSubGroup.isSomeChecked = subletSubGroup.subletItems.some((item: ISubletItem) => item.isChecked);
      subletSubGroup.isSomeCompleted = subletSubGroup.subletItems.some((item: ISubletItem) => item.isChecked && item?.workStatus?.value === 'completed');
    }
    if (!['workStatus', 'isChecked', 'allWorkStatus'].includes(key)) {
      subletSubGroup.isAllCompleted = subletSubGroup.subletItems.every((item: ISubletItem) => item?.workStatus?.value === 'completed');
    }

    if (key !== 'isChecked') {
      this.hasUnsavedChanges = true;
    }
  }

  handleChange(type: string, event: any, subletGroup: ISubletGroup) {
    if (type === 'supplier') {
      if (!subletGroup?.oldSupplierId && subletGroup[type].supplierId !== event?._id) {
        subletGroup.oldSupplierId = subletGroup[type].supplierId;
        subletGroup.isSuplierTouched = true;
      } else if (subletGroup.oldSupplierId === event?._id) {
        subletGroup.isSuplierTouched = false;
      }
      subletGroup[type] = { name: event?.name, supplierId: event?._id };
      if (event?.accPayableTerm?._id) {
        this.handleChange('accPayableTerm', event.accPayableTerm, subletGroup);
      }
    } else {
      subletGroup[type] = event;
    }
    this.hasUnsavedChanges = true;
  }

  handleSubGroupChange(type: string, event: any, subletSubGroup: ISubSubletGroup, subletGroup: ISubletGroup) {
    if (type === 'isAllChecked') {
      subletSubGroup.subletItems.forEach((item: ISubletItem) => {
        this.handleSubletItemChange('isChecked', event, item, subletSubGroup);
      });
    } else if (type === 'allWorkStatus') {
      subletSubGroup.subletItems.forEach((item: ISubletItem) => {
        this.handleSubletItemChange('workStatus', event, item, subletSubGroup);
      });
    } else if (['invoiceDate', 'accPayableTerm'].includes(type)) {
      subletSubGroup.invoiceDateErrMsg = '';
      subletSubGroup.dueDateErrMsg = '';
      this.setDueDate(subletSubGroup, subletGroup);
    } else {
      subletSubGroup[type] = event;
    }

    if (type !== 'isAllChecked') {
      this.hasUnsavedChanges = true;
    }
  }

  mapWorkStatus(status: any) {
    return status && typeof status === 'string' ? find(this.assignStatusOptions, { value: status }) : this.assignStatusOptions[0];
  }

  mapAccPayableTerms(id: string) {
    const obj = find(this.paymentTerms, { _id: id }) || { type: '', _id: '', name: '' };
    return obj;
  }

  getPaidTimeTooltip(innerTooltip: string) {
    return `
      <div class="flex-column">
        <div class="border-bottom-light flex-start-center font-secondary-800 gap-2 p-2">
          <i class="fal fa-money-bill-alt font-14"></i>
          <span class="font-14 font-700">Paid Date &amp; Time</span>
        </div>
        <div class="flex-column gap-2 p-2 font-gray-35">
          ${innerTooltip}
        </div>
      </div>`;
  }

  createPaidDateTimeTooltip(subletTitle: string, invoiceDate: any) {
    const paidCartTooltip = `
            <div class="flex-row flex-start-center">
              <div class="flex-start-center gap-2 ui-g-8 p-0">
                <i class="fal fa-file-alt font-12"></i>
                <span class="font-12 font-800 text-truncate">
                  ${subletTitle}
                </span>
              </div>

              <div class="flex-start-center gap-2 ui-g-2 p-0">
                <i class="fal fa-calendar font-12"></i>
                <span class="font-12 font-400">
                  ${this.dateService.formatDate(invoiceDate, datetimeFormat.dddmmmyy)}
                </span>
              </div>

              <div class="flex-end-center gap-2 ui-g-2 p-0">
                <i class="fal fa-clock font-12"></i>
                <span class="font-12 font-400">
                  ${this.dateService.formatDate(invoiceDate, datetimeFormat.hhmmA)}
                </span>
              </div>
            </div>`;

    return paidCartTooltip;
  }

  formatSubletItems(subletCartArray: any[]): ISubletGroup[] {
    let subletGroupObj: ISubletGroup = {
      supplierGroupId: '',
      subGroups: [],
      supplier: {
        name: '',
        supplierId: '',
      },
      accPayableTerm: {
        type: '',
        _id: '',
        name: '',
      },
      notes: '',
      groupCategoryStatus: 'unassign',
      supplierErrMsg: '',
      paymentTermErrMsg: '',
      isSuplierTouched: false,
      oldSupplierId: '',
    };
    let subletSubGroupObj: ISubSubletGroup = {
      subletItems: [],
      categoryStatus: '',
      cartId: '',
      isAllChecked: false,
      isSomeChecked: false,
      isSomeCompleted: false,
      isAllCompleted: false,
      isSubmitting: false,
      isUpdating: false,
      isPosting: false,
      paidStatus: '',
      invoiceDate: '',
      dueDate: '',
      supplierInvoicedNo: '',
      qboId: '',
      isSyncedWithQBO: false,
      allWorkStatus: '',
      totals: {
        subTotal: 0,
        tax: {},
        taxedAmount: 0,
        nonTaxableAmount: 0,
        taxableAmount: 0,
        total: 0,
      },
      invoiceDateErrMsg: '',
      dueDateErrMsg: '',
      invoiceNoErrMsg: '',
      qboOptions: [],
      cartTotals: {
        totalCost: 0,
        totalPrice: 0,
        grandTotal: 0,
      },
      paidDateTimeTooltip: '',
    };
    let cartObj: any = {};

    if (subletCartArray && subletCartArray.length) {
      subletCartArray.forEach((subletCart: any) => {
        let subGroupsObj: ISubSubletGroup = this.helperService.deepCopy(subletSubGroupObj);
        let tooltip: string = '';
        subletCart.items.forEach((item: any) => {
          if (item.itemType === 'sublet') {
            if (!subletCart?.partsOrder?.supplier?.supplierId) {
              subletCart.supplierGroupId = 'unassigned-cart';
            } else {
              subletCart.supplierGroupId = subletCart?.partsOrder?.supplier?.supplierId;
            }
            if (!cartObj[subletCart.supplierGroupId]) {
              cartObj[subletCart.supplierGroupId] = this.helperService.deepCopy(subletGroupObj);
            }

            cartObj[subletCart.supplierGroupId].supplierGroupId = subletCart?.partsOrder?.supplier?.supplierId;
            cartObj[subletCart.supplierGroupId].supplier = subletCart?.partsOrder?.supplier || { name: '', supplierId: '' };
            cartObj[subletCart.supplierGroupId].accPayableTerm = this.mapAccPayableTerms(subletCart?.partsOrder?.accPayableTerm?._id);
            cartObj[subletCart.supplierGroupId].notes = subletCart?.partsOrder?.notes;
            cartObj[subletCart.supplierGroupId].groupCategoryStatus = subletCart?.categoryStatus;

            let itemObj: ISubletItem = {
              isChecked: false,
              title: item.title,
              cost: item.cost,
              price: item.price,
              //Changed against ALP-20466 always give the priority to service item
              //isTaxable: subletCart?.supplierGroupId === 'unassigned-cart' ? subletCart?.partsOrder?.isSupplierSpecificSubletService : item.isTaxable || false,
              isTaxable: item?.isTaxable || false,
              count: item.quantity,
              subTotal: item.subTotal,
              workStatus: this.mapWorkStatus(item?.workStatus || ''),
              cartId: item?.cartId,
              itemId: item.itemId,
              itemInternalReference: item?.itemInternalReference,
              totalCost: item?.totalCost,
              salesCode: item?.salesCode,
            };

            if (subletCart?.cartId === item?.cartId) {
              subGroupsObj.subletItems.push(itemObj);
              subGroupsObj.invoiceDate = subletCart?.partsOrder?.invoiceDate ? this.dateService.getCurrentDateTimeTz(new Date(subletCart?.partsOrder?.invoiceDate).getTime()) : '';
              subGroupsObj.dueDate = subletCart?.partsOrder?.dueDate ? this.dateService.getCurrentDateTimeTz(new Date(subletCart?.partsOrder?.dueDate).getTime()) : '';
              subGroupsObj.supplierInvoicedNo = subletCart?.partsOrder?.supplierInvoicedNo;
              subGroupsObj.cartId = subletCart?.cartId;
              subGroupsObj.categoryStatus = subletCart?.categoryStatus;
              subGroupsObj.paidStatus = subletCart?.partsOrder?.paidStatus;
              subGroupsObj.isAllChecked = false;
              subGroupsObj.isSomeChecked = false;
              subGroupsObj.isSomeCompleted = false;
              subGroupsObj.isSubmitting = false;
              subGroupsObj.totals = subletCart?.partsOrder?.total;
              subGroupsObj.qboId = subletCart?.partsOrder?.qboId;
              subGroupsObj.isSyncedWithQBO = subletCart?.partsOrder?.isSyncedWithQBO;
              subGroupsObj.cartTotals.totalCost += item.cost;
              subGroupsObj.cartTotals.totalPrice += item.price;
              subGroupsObj.cartTotals.grandTotal += item.totalCost;
              if (subGroupsObj.paidStatus === 'paid') {
                tooltip += this.createPaidDateTimeTooltip(item.title, item?.paidStatusDate ? this.dateService.getCurrentDateTimeTz(new Date(item?.paidStatusDate).getTime()) : '');
              }
            }
          }
        });
        if (subGroupsObj.paidStatus === 'paid') {
          subGroupsObj.paidDateTimeTooltip = this.getPaidTimeTooltip(tooltip);
        }
        subGroupsObj.qboOptions = this.getQuickBookOptions(subletCart?.partsOrder?.qboId);
        subGroupsObj.isAllCompleted = subGroupsObj.subletItems.every((item: ISubletItem) => item?.workStatus?.value === 'completed');
        cartObj[subletCart.supplierGroupId].subGroups.push(subGroupsObj);
      });
    }
    //console.log(cartObj);
    return Object.values(cartObj);
  }

  setFeatureSettings() {
    this.isAccountPayable = this.dataService.accountPayablePayment;
    this.isSalesCodeEnabled = this.dataService.isSalesCodeEnabled;

    if (this.dataService.accPayableTerms && this.dataService.accPayableTerms.length) {
      const isQuickbooksConnected = this.helperService.getCompany().qboId;

      this.paymentTerms = this.dataService.accPayableTerms.filter(({ qboId }) => {
        if (isQuickbooksConnected) {
          return !!qboId;
        } else {
          return !qboId;
        }
      });
    }
  }

  openAssignSalesCodeDialog(item) {
    // this.errors.salesCode = false;
    const data = {
      item,
      openFrom: 'sublet-item',
    };
    // data.item.title = data.item?.title || '';
    this.dataService.sendData(this.dataService.typeDialog('open-sales-code-assign-modal', 'ro-fee', null, null, data));
  }

  displaySalesCodeTitle(item) {
    return this.helperService.displaySalesCodeTitle(item?.salesCode, item?.category);
  }

  handleDiscardChanges() {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: `Unsaved changes?`,
        message: `There are unsaved changes here. Would you like to save them before leaving this page?`,
        button: {
          success: { title: 'Dismiss' },
          cancel: { title: 'Discard changes' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess }) => {
      if (!isSuccess) {
        this.handleClose(true);
      }
      onClose.unsubscribe();
    });
  }

  openSuplierConfirmationDialog(obj: any, subletGroup: ISubletGroup) {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog sublet-supplier-customized-confirmation',
      showHeader: false,
      data: {
        isCustomizedConfirmationBox: true,
        isHTML: true,
        styleClassHeading: 'font-18 font-700',
        styleClassCloseIcon: 'font-24 font-400 font-gray',
        title: `Internal supplier not selected`,
        message: `
        <span>
          <p>I understand that this will create a new supplier in QBO and this action cannot be reversed.</p>
        </span>`,
        button: {
          success: {
            title: 'Confirm',
            styleClass: 'btn-primary',
          },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess }) => {
      if (isSuccess) {
        this.createSupplier(obj, subletGroup);
      } else {
        const oldSupplier = find(this.suppliersResults, { _id: subletGroup.oldSupplierId });
        this.handleChange('supplier', oldSupplier, subletGroup);
      }
      onClose.unsubscribe();
    });
  }

  saveSupplier({ name }, subletGroup: ISubletGroup) {
    if (name) {
      const obj = {
        name,
        type: 'sublet',
      };
      this.openSuplierConfirmationDialog(obj, subletGroup);
    }
  }

  createSupplier(data: any, subletGroup: ISubletGroup) {
    this.httpService.createSupplier(data).then(
      (supplier: any) => {
        if (supplier) {
          this.helperService.showToaster('success', messages.supplierCreated);
          this.suppliers.push({ ...supplier });
          this.handleChange('supplier', supplier, subletGroup);
        }
      },
      (err) => {
        this.helperService.showToaster('error', err.messages ? err.messages : err);
      },
    );
  }

  searchSuggestion(query: string, type?: string) {
    this.suppliersResults = [];
    query = this.helperService.trimString(query);
    if (type === 'accPayableTerm') {
      this.paymentTerms = this.dataService.accPayableTerms.filter(({ label }) => {
        if (label && new RegExp(`${query}`, 'gi').test(label)) {
          return true;
        } else {
          return false;
        }
      });
      this.paymentTerms = [...this.paymentTerms];
    }
    if (type === 'supplier') {
      query = this.helperService.trimString(query);
      this.suppliersResults = this.suppliers.filter(({ name }) => {
        if (name && new RegExp(`${query}`, 'gi').test(name)) {
          return true;
        } else {
          return false;
        }
      });
      this.suppliersResults = [...this.suppliersResults];
    }
  }

  onUpdateSupplier(data: any, subletGroup: ISubletGroup) {
    this.httpService.updateSupplier(data).then(
      (res: any) => {
        for (let i = 0; i < this.suppliers.length; i++) {
          if (this.suppliers[i]._id === res._id) {
            this.suppliers[i].name = data.name;
            break;
          }
        }

        for (let i = 0; i < this.suppliersResults.length; i++) {
          if (this.suppliersResults[i]._id === res._id) {
            this.suppliersResults[i].name = data.name;
            if (subletGroup.supplier.supplierId === res._id) {
              subletGroup.supplier.name = data.name;
            }
            break;
          }
        }
        this.helperService.showToaster('success', messages.successfullyUpdated);
      },
      (err) => {
        this.helperService.showToaster('error', err);
      },
    );
  }

  scrollToBottom() {
    setTimeout(() => {
      try {
        this.subletSupplierBody.nativeElement.scrollTop = this.subletSupplierBody.nativeElement.scrollHeight + 18;
      } catch (err) {}
    }, 100);
  }

  onClearSuggestion($event?: any) {
    this.suppliersResults = [...this.suppliers];
  }

  setSuppliers(suppliers: any[]) {
    this.suppliersResults = [...suppliers];
  }

  showSuggestions() {
    this.suppliersResults = [...this.suppliers];
    this.scrollToBottom();
  }

  getSuppliers() {
    const conf = {
      skip: 0,
      page: 1,
      limit: Number.MAX_SAFE_INTEGER,
      sortField: 'name',
      sortOrder: 1,
      activeStatus: true,
      supplierType: ['sublet'],
    };
    this.isLoadingSuppliers = true;
    this.httpService
      .getSupplierList(conf)
      .then(
        (data: any) => {
          if (data.records) {
            this.suppliers = data.records.filter((items) => items.type !== 'online');
            this.setSuppliers(data.records);
            this.suppliersResults = [...this.suppliers];
          }
        },
        (error) => {
          this.helperService.showToaster('error', error);
        },
      )
      .finally(() => {
        this.isLoadingSuppliers = false;
      });
  }

  handleFocus(subletGroup: ISubletGroup, errorKey: string) {
    if (subletGroup[errorKey]) {
      subletGroup[errorKey] = '';
    }
  }

  setDueDate(subletSubGroup: ISubSubletGroup, subletGroup: ISubletGroup) {
    const groupInvoiceDate = subletSubGroup?.invoiceDate;
    const accPayableTerm = subletGroup?.accPayableTerm;

    if (accPayableTerm?._id && groupInvoiceDate) {
      if (accPayableTerm.type === 'fixDays') {
        const dueDate = this.dateService.addDaysInDatetime(groupInvoiceDate, accPayableTerm.dueDate, 'day', '', false);

        subletSubGroup.dueDate = dueDate;
      } else {
        const invoiceDate = moment(groupInvoiceDate);
        let dueDate = moment(invoiceDate).set('D', accPayableTerm.dueDate);
        let i = 0;

        do {
          let monthBeforeSettingDate = 0;
          dueDate = moment(invoiceDate).add(i, 'M');
          monthBeforeSettingDate = dueDate.get('month');
          dueDate = dueDate.set('D', accPayableTerm.dueDate);

          if (dueDate.get('month') !== monthBeforeSettingDate) {
            dueDate = moment(invoiceDate).endOf('month');
          }

          i++;
        } while (dueDate.diff(invoiceDate, 'days') < accPayableTerm.dueDateNextMonth || i > 100);

        subletSubGroup.dueDate = dueDate.toDate();
      }
    }
  }

  handleQuickbookAction(subletGroup: ISubletGroup, subletSubGroup: ISubSubletGroup, event: any) {
    if (event.value === 'sync-quickbooks') {
      this.postCartToQbo(subletGroup, subletSubGroup);
    } else if (event.value !== 'sync-quickbooks' && subletSubGroup?.qboId) {
      this.openInQuickbooks(subletSubGroup?.qboId);
    }
  }

  getQuickBookOptions(qboId: string): any {
    if (qboId) {
      return [
        { value: 'sync-quickbooks', title: 'Update to Quickbooks' },
        { value: 'show-in-quickbooks', title: 'Show in Quickbooks' },
      ];
    }

    return [{ value: 'sync-quickbooks', title: 'Post to Quickbooks' }];
  }

  openInQuickbooks(qboId?: string) {
    window.open(`${environment.quickbooks_base_url}/bill?txnId=${qboId}`, '_blank');
  }

  handleInputFocus(key: string, subletItem: ISubletItem, isFocus: boolean) {
    subletItem[key] = isFocus;
    if (key === 'isCostFocus' && !isFocus) {
      const price: number = this.calculatePricingMatrix(subletItem.cost);
      if (price) {
        subletItem.price = this.calculatePrice(subletItem.count, price);
      }
    }
  }

  handleValidation(subletGroup: ISubletGroup, subletSubGroup: ISubSubletGroup, validateAll?: boolean) {
    let isValid: boolean = true;
    if (!subletGroup?.supplier?.name) {
      subletGroup.supplierErrMsg = 'Supplier required';
      isValid = false;
    }

    if (subletGroup.isSuplierTouched && subletSubGroup.qboId) {
      this.helperService.showToaster('error', 'Supplier cannot be updated once posted to Qbo.');
      subletGroup.supplierErrMsg = 'Supplier cannot be updated once posted to Qbo.';
      isValid = false;
    }

    if (validateAll) {
      if (!subletSubGroup?.invoiceDate || subletSubGroup?.invoiceDate === '1970-01-01') {
        subletSubGroup.invoiceDateErrMsg = 'Valid invoice date is required';
        isValid = false;
      }

      if (!subletSubGroup?.dueDate || subletSubGroup?.dueDate === '1970-01-01') {
        subletSubGroup.dueDateErrMsg = 'Valid due date is required';
        isValid = false;
      }

      if (!subletSubGroup?.supplierInvoicedNo) {
        subletSubGroup.invoiceNoErrMsg = 'Invoice number is required';
        isValid = false;
      }

      if (!subletGroup?.accPayableTerm?.name) {
        subletGroup.paymentTermErrMsg = 'Payment term is required';
        isValid = false;
      }
    }

    return isValid;
  }

  assignSupplier(subletSubGroup: ISubSubletGroup, subletGroup: ISubletGroup) {
    const isValid = this.handleValidation(subletGroup, subletSubGroup);
    if (isValid) {
      this.isReloadNeeded = true;
      let payload = this.formatPayload(subletSubGroup, subletGroup);
      payload.actionType = 'assignSupplier';
      this._updateCart(subletSubGroup, payload, 'Supplier assigned successfully.');
    }
  }

  _updateCart(subletSubGroup: ISubSubletGroup, payload: any, successMsg: string, triggerGainSight = true) {
    if (payload.actionType === 'updateCartItems') {
      subletSubGroup.isUpdating = true;
    } else {
      subletSubGroup.isSubmitting = true;
    }

    this.httpService
      .updateSubletSupplier(payload, this.orderId)
      .then(
        (res: any) => {
          this.helperService.showToaster('success', successMsg);
          this.getSubletItems(this.orderId, false);

          if (triggerGainSight && this.dataService.isGainSightPXEnabled) {
            const dateTimeObj: any = this.helperService.getCurrentTimezoneDateTime();
            for (const item of subletSubGroup.subletItems) {
              if (payload?.items[item.itemInternalReference]?.workStatus === 'completed') {
                const gainSightObj = {
                  subletId: item.itemId,
                  sublet: item.title,
                  suplier: payload.supplier.name,
                  roNum: this.roNum,
                  createdTime: this.datePipe.transform(dateTimeObj, 'h:mm:ssa'),
                };

                (<any>window).aptrinsic('track', 'Sublet Completed', gainSightObj);
              }
            }
          }
        },
        (err) => {
          this.helperService.showToaster('error', err);
        },
      )
      .finally(() => {
        subletSubGroup.isSubmitting = false;
        subletSubGroup.isUpdating = false;
      });
  }

  updateSubletSubGroup(subletGroup: ISubletGroup, subletSubGroup: ISubSubletGroup) {
    const isValid = this.handleValidation(subletGroup, subletSubGroup);
    if (isValid) {
      this.isReloadNeeded = true;
      let payload = this.formatPayload(subletSubGroup, subletGroup);
      payload.actionType = 'updateCartItems';
      this._updateCart(subletSubGroup, payload, 'Sublet items updated successfully.');
    }
  }

  markStatus(subletSubGroup: ISubSubletGroup, subletGroup: ISubletGroup, status: string) {
    const isValid = this.handleValidation(subletGroup, subletSubGroup);
    if (isValid) {
      this.isReloadNeeded = true;
      subletSubGroup.paidStatus = status;
      let payload = this.formatPayload(subletSubGroup, subletGroup);
      payload.actionType = 'updatePaidStatus';
      payload.items = this.mapSelectedSubletItems(subletSubGroup, subletGroup, true);
      this._updateCart(subletSubGroup, payload, `Sublet item is successfully marked as ${status}`, false);
      if (status === 'paid' && this.dataService.isGainSightPXEnabled) {
        const dateTimeObj: any = this.helperService.getCurrentTimezoneDateTime();
        for (const item of subletSubGroup.subletItems) {
          const gainSightObj = {
            subletId: item.itemId,
            sublet: item.title,
            suplier: payload.supplier.name,
            roNum: this.roNum,
            createdTime: this.datePipe.transform(dateTimeObj, 'h:mm:ssa'),
          };
          (<any>window).aptrinsic('track', 'Sublet Paid', gainSightObj);
        }
      }
    }
  }

  formatPayload(subSubletGroup: ISubSubletGroup, subletGroup: ISubletGroup): any {
    let obj: any = {
      supplier: subletGroup.supplier,
      cartId: subSubletGroup.cartId,
      accPayableTerm: subletGroup.accPayableTerm,
      notes: subletGroup.notes || '',
      paidStatus: subSubletGroup.paidStatus,
      invoiceDate: subSubletGroup.invoiceDate ? this.dateService.formatDate(subSubletGroup.invoiceDate, datetimeFormat.yyymmdd) : '',
      dueDate: subSubletGroup.dueDate ? this.dateService.formatDate(subSubletGroup.dueDate, datetimeFormat.yyymmdd) : '',
      supplierInvoicedNo: subSubletGroup.supplierInvoicedNo,
      items: this.mapSelectedSubletItems(subSubletGroup, subletGroup),
    };
    return obj;
  }

  getSubletItems(orderId: string, showLoader: boolean = true) {
    this.isLoading = showLoader;
    this.httpService
      .getSubletItems(orderId)
      .then(
        (res: any) => {
          if (res?.length > 0) {
            this.subletGroupsArray = this.formatSubletItems(res);
            if (this.itemReferenceId && this.subletGroupsArray?.length) {
              this.markedServiceSubletItem();
            }
            this.itemReferenceId = '';
          }
        },
        (err) => {
          this.helperService.showToaster('error', err);
        },
      )
      .finally(() => {
        this.isLoading = false;
        this.hasUnsavedChanges = false;
      });
  }

  markedServiceSubletItem() {
    this.subletGroupsArray.forEach((cartGroup: ISubletGroup) => {
      if (cartGroup.subGroups && cartGroup.subGroups.length) {
        cartGroup.subGroups.forEach((subGroup: ISubSubletGroup) => {
          if (!subGroup.invoiceDate) {
            subGroup.invoiceDate = new Date();
          }
          if (subGroup.subletItems && subGroup.subletItems.length) {
            subGroup.subletItems.forEach((subletItem: ISubletItem) => {
              if (this.itemReferenceId === subletItem.itemInternalReference) {
                this.handleSubletItemChange('isChecked', true, subletItem, subGroup);
              }
            });
          }
        });
      }
    });
  }

  postCartToQbo(subletGroup: ISubletGroup, subletSubGroup: ISubSubletGroup) {
    const isValid = this.handleValidation(subletGroup, subletSubGroup, true);
    if (isValid) {
      const payload: any = {
        sourceId: subletSubGroup.cartId,
        sourceType: 'sublet',
        invoiceDate: subletSubGroup.invoiceDate ? this.dateService.formatDate(subletSubGroup.invoiceDate, datetimeFormat.yyymmdd) : '',
        dueDate: subletSubGroup.dueDate ? this.dateService.formatDate(subletSubGroup.dueDate, datetimeFormat.yyymmdd) : '',
        supplierInvoicedNo: subletSubGroup.supplierInvoicedNo,
      };
      subletSubGroup.isPosting = true;
      this.httpService
        .postSubletCartToQbo(payload, this.orderId)
        .then(
          (res: any) => {
            this.helperService.showToaster('success', 'Cart is successfully posted to Qbo');
            this.getSubletItems(this.orderId, false);

            if (this.dataService.isGainSightPXEnabled) {
              for (const item of subletSubGroup.subletItems) {
                const gainSightObj = {
                  subletId: item.itemId,
                  sublet: item.title,
                  suplier: subletGroup.supplier.name,
                  roNum: this.roNum,
                  invoiceDate: this.datePipe.transform(subletSubGroup.invoiceDate, datetimeFormat.mmmdyyyy),
                  dueDate: this.datePipe.transform(subletSubGroup.dueDate, datetimeFormat.mmmdyyyy),
                  invoiceNum: subletSubGroup.supplierInvoicedNo,
                };

                (<any>window).aptrinsic('track', 'Sublet Posted as Bill to QBO', gainSightObj);
              }
            }
          },
          (err: any) => {
            this.helperService.showToaster('error', err?.msg?.body || err?.msg);
          },
        )
        .finally(() => {
          subletSubGroup.isPosting = false;
        });
    }
  }
}
